import {
  Search,
  CheckBox,
  CheckBoxOutlineBlank,
  ExpandMore,
  ExpandLess,
  Settings,
} from "@mui/icons-material";
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormHelperText,
  InputAdornment,
  Link,
  ListSubheader,
  Paper,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { hostname, stringToVintageColor } from "../utils";
import SenderProfilesDisplay, { SenderProfile } from "./SenderProfilesDisplay";

/**
 * Load all sender profiles for a company, handling pagination automatically.
 * Fetches up to 1000 profiles per request and continues fetching if there are more.
 */
export async function loadAllSenderProfiles(
  token: string,
  companyId: string
): Promise<SenderProfile[]> {
  const allProfiles: SenderProfile[] = [];
  let page = 1;
  const pageSize = 1000;
  let hasMore = true;

  while (hasMore) {
    const res = await axios.get(`${hostname}/sequences/profiles`, {
      params: { companyId, page, pageSize },
      headers: { Authorization: `Bearer ${token}` },
    });

    const items = res.data.items || [];
    allProfiles.push(...items);

    // Check if there are more pages
    const total = res.data.total || 0;
    hasMore = allProfiles.length < total;
    page++;

    // Safety limit to prevent infinite loops
    if (page > 100) break;
  }

  return allProfiles;
}

// Multi-select props
interface MultiSelectProps {
  multiple: true;
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  selectedId?: never;
  onChangeSingle?: never;
}

// Single-select props
interface SingleSelectProps {
  multiple?: false;
  selectedId: string;
  onChangeSingle: (selectedId: string) => void;
  selectedIds?: never;
  onChange?: never;
}

interface BaseProps {
  profiles: SenderProfile[];
  label?: string;
  helperText?: string | React.ReactNode;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  onManageProfiles?: () => void;
}

type SearchableSenderProfileSelectProps = BaseProps & (MultiSelectProps | SingleSelectProps);

interface GroupedProfiles {
  domain: string;
  profiles: SenderProfile[];
}

const SearchableSenderProfileSelect: React.FC<
  SearchableSenderProfileSelectProps
> = (props) => {
  const {
    profiles,
    label = "Sender Profile",
    helperText,
    disabled = false,
    error = false,
    fullWidth = true,
    loading = false,
    onManageProfiles,
  } = props;

  // Normalize to work with arrays internally
  const isMultiple = props.multiple === true;
  const selectedIds = isMultiple ? props.selectedIds : (props.selectedId ? [props.selectedId] : []);
  const handleChange = (newIds: string[]) => {
    if (isMultiple) {
      props.onChange(newIds);
    } else {
      props.onChangeSingle(newIds.length > 0 ? newIds[0] : "");
    }
  };

  const [inputValue, setInputValue] = useState("");

  // Group profiles by email domain
  const groupedProfiles = useMemo(() => {
    const groups: Record<string, SenderProfile[]> = {};

    profiles.forEach((profile) => {
      const domain = profile.email?.split("@")[1] || "No Email";
      if (!groups[domain]) {
        groups[domain] = [];
      }
      groups[domain].push(profile);
    });

    // Sort domains alphabetically, then sort profiles within each domain
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([domain, domainProfiles]) => ({
        domain,
        profiles: domainProfiles.sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }, [profiles]);

  // Initialize collapsed domains with all domains collapsed by default
  const [collapsedDomains, setCollapsedDomains] = useState<Set<string>>(
    () => new Set(groupedProfiles.map((g) => g.domain))
  );

  // Update collapsed domains when new domains appear
  useEffect(() => {
    const currentDomains = new Set(groupedProfiles.map((g) => g.domain));
    setCollapsedDomains((prev) => {
      const newDomains = [...currentDomains].filter((d) => !prev.has(d));
      if (newDomains.length > 0) {
        return new Set([...prev, ...newDomains]);
      }
      return prev;
    });
  }, [groupedProfiles]);

  // Flatten profiles with domain info for autocomplete
  const flattenedOptions = useMemo(() => {
    const options: Array<SenderProfile & { domain: string }> = [];
    groupedProfiles.forEach(({ domain, profiles: domainProfiles }) => {
      domainProfiles.forEach((profile) => {
        options.push({ ...profile, domain });
      });
    });
    return options;
  }, [groupedProfiles]);

  const selectedProfiles = profiles.filter((p) => selectedIds.includes(p.id));

  const toggleDomainCollapse = (domain: string) => {
    setCollapsedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) {
        next.delete(domain);
      } else {
        next.add(domain);
      }
      return next;
    });
  };

  const selectAllInDomain = (domain: string) => {
    // Only available in multi-select mode
    if (!isMultiple) return;

    const domainProfileIds = profiles
      .filter((p) => (p.email?.split("@")[1] || "No Email") === domain)
      .map((p) => p.id);

    const allSelected = domainProfileIds.every((id) =>
      selectedIds.includes(id)
    );

    if (allSelected) {
      // Deselect all in domain
      handleChange(selectedIds.filter((id) => !domainProfileIds.includes(id)));
    } else {
      // Select all in domain
      const newIds = [...new Set([...selectedIds, ...domainProfileIds])];
      handleChange(newIds);
    }
  };

  const handleToggle = (profile: SenderProfile) => {
    const isSelected = selectedIds.includes(profile.id);
    if (isMultiple) {
      if (isSelected) {
        handleChange(selectedIds.filter((id) => id !== profile.id));
      } else {
        handleChange([...selectedIds, profile.id]);
      }
    } else {
      // Single select - just set the value (or clear if clicking selected)
      handleChange(isSelected ? [] : [profile.id]);
    }
  };

  // For single select, get the single selected profile
  const singleSelectedProfile = !isMultiple && selectedIds.length > 0
    ? flattenedOptions.find(p => p.id === selectedIds[0]) || null
    : null;

  return (
    <FormControl fullWidth={fullWidth} error={error}>
      <Autocomplete
        multiple={isMultiple}
        disableCloseOnSelect={isMultiple}
        disabled={disabled || loading}
        loading={loading}
        options={flattenedOptions}
        value={isMultiple ? selectedProfiles : singleSelectedProfile}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        onChange={(_, newValue) => {
          if (isMultiple) {
            handleChange((newValue as SenderProfile[]).map((p) => p.id));
          } else {
            handleChange(newValue ? [(newValue as SenderProfile).id] : []);
          }
        }}
        getOptionLabel={(option) => `${option.name} (${option.email})`}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        groupBy={(option) => (option as SenderProfile & { domain: string }).domain}
        filterOptions={(options, { inputValue }) => {
          const searchLower = inputValue.toLowerCase();
          return options.filter(
            (option) =>
              option.name.toLowerCase().includes(searchLower) ||
              option.email.toLowerCase().includes(searchLower)
          );
        }}
        PaperComponent={({ children, ...props }) => (
          <Paper {...props} sx={{ maxHeight: 400 }}>
            {children}
          </Paper>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={selectedIds.length === 0 ? (loading ? "" : "Search profiles...") : ""}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {params.InputProps.startAdornment}
                  {selectedIds.length === 0 && !loading && (
                    <InputAdornment position="start">
                      <Search fontSize="small" color="action" />
                    </InputAdornment>
                  )}
                  {loading && (
                    <InputAdornment position="start">
                      <Typography variant="body2" color="text.secondary">
                        Loading...
                      </Typography>
                    </InputAdornment>
                  )}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        {...(isMultiple && {
          renderTags: (value: SenderProfile[]) => (
            <SenderProfilesDisplay
              profiles={profiles}
              selectedIds={value.map((p) => p.id)}
              maxVisible={3}
              maxNamesVisible={3}
              size="small"
              showEmails={false}
              showNames={true}
            />
          ),
        })}
        renderGroup={(params) => {
          const domain = params.group;
          const domainProfileIds = profiles
            .filter((p) => (p.email?.split("@")[1] || "No Email") === domain)
            .map((p) => p.id);
          const selectedInDomain = domainProfileIds.filter((id) =>
            selectedIds.includes(id)
          ).length;
          const allSelected =
            domainProfileIds.length > 0 &&
            selectedInDomain === domainProfileIds.length;
          const someSelected = selectedInDomain > 0 && !allSelected;
          const isCollapsed = collapsedDomains.has(domain);

          return (
            <Box key={params.key}>
              <ListSubheader
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: theme.palette.mode === "dark"
                    ? theme.palette.grey[800]
                    : theme.palette.grey[200],
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: theme.palette.mode === "dark"
                      ? theme.palette.grey[700]
                      : theme.palette.grey[300],
                  },
                  py: 0.5,
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleDomainCollapse(domain);
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {isMultiple && (
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onClick={(e) => {
                        e.stopPropagation();
                        selectAllInDomain(domain);
                      }}
                      icon={<CheckBoxOutlineBlank fontSize="small" />}
                      checkedIcon={<CheckBox fontSize="small" />}
                    />
                  )}
                  <Typography variant="subtitle2" fontWeight="bold">
                    @{domain}
                  </Typography>
                  {isMultiple && (
                    <Chip
                      label={`${selectedInDomain}/${domainProfileIds.length}`}
                      size="small"
                      color={allSelected ? "primary" : "default"}
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  )}
                  {!isMultiple && (
                    <Chip
                      label={domainProfileIds.length}
                      size="small"
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  )}
                </Box>
                {isCollapsed ? (
                  <ExpandMore fontSize="small" />
                ) : (
                  <ExpandLess fontSize="small" />
                )}
              </ListSubheader>
              <Collapse in={!isCollapsed}>{params.children}</Collapse>
            </Box>
          );
        }}
        renderOption={(props, option, { selected }) => {
          const { key, ...otherProps } = props;
          const warmupStats = option.warmupStats;
          const hasWarmup = warmupStats !== null && warmupStats !== undefined;
          const healthScore = hasWarmup
            ? warmupStats.health_score_label || "0%"
            : null;
          const healthValue = healthScore ? parseInt(healthScore) : 0;

          return (
            <Box
              component="li"
              key={key}
              {...otherProps}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 0.5,
                px: 1,
              }}
              onClick={(e) => {
                e.preventDefault();
                handleToggle(option);
              }}
            >
              {isMultiple ? (
                <Checkbox
                  checked={selected}
                  size="small"
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBox fontSize="small" />}
                />
              ) : (
                <Radio
                  checked={selected}
                  size="small"
                />
              )}
              <Avatar
                src={option.picture}
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: "0.8rem",
                  bgcolor: stringToVintageColor(option.name),
                }}
              >
                {option.name?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" noWrap>
                    {option.name}
                  </Typography>
                  {hasWarmup && (
                    <Chip
                      label={`Health: ${healthScore}`}
                      size="small"
                      color={
                        healthValue >= 80
                          ? "primary"
                          : healthValue >= 50
                          ? "warning"
                          : "error"
                      }
                      sx={{ height: 18, fontSize: "0.65rem" }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {option.email}
                </Typography>
                {hasWarmup && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ fontSize: "0.65rem" }}
                  >
                    Sent: {warmupStats.sent || 0} | Inbox:{" "}
                    {warmupStats.landed_inbox || 0} | Received:{" "}
                    {warmupStats.received || 0}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }}
      />
      {(helperText || onManageProfiles) && (
        <FormHelperText component="div">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {helperText && <span>{helperText}</span>}
            {onManageProfiles && (
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={(e) => {
                  e.preventDefault();
                  onManageProfiles();
                }}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <Settings sx={{ fontSize: 14 }} />
                Manage sender profiles
              </Link>
            )}
          </Box>
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default SearchableSenderProfileSelect;
